/**
 * Demo-wide: anything that can only work on a real account (a server upload, a payment, an email or text, Google) never shows
 * the server's raw refusal. It shows one friendly card (components/DemoTapGate.tsx DemoOnlyCard): "Try this in your restaurant",
 * what it does, "Sign up free", "Keep exploring the demo". Implemented once: `withDemoOnly` turns "needs the server" errors
 * (unauthenticated, unavailable, offline, a failed fetch) into a DemoOnlyError and tells the card; `guardDemoLinks` catches an
 * anchor or image that points at an API URL (a navigation there would show the API's JSON). Only the small part is in the main
 * bundle; the link guard and the card load when the sample opens (the demo bundle budget).
 */
import { ApiError, type Api } from "../api.ts";

export const DEMO_ONLY_EVENT = "cc:demo-only";
export const DEMO_ONLY_LINE = "This part needs your own account. Try it at your place.";

/** The error a demo-only call throws: plain words, never a JSON body. */
export class DemoOnlyError extends ApiError {
  constructor(public from: string) { super(403, "demo_only", DEMO_ONLY_LINE); this.name = "DemoOnlyError"; }
}

/** A refusal that only exists because the demo has no server behind this call. */
export function isDemoOnlyFailure(e: unknown): boolean {
  if (e instanceof ApiError) return !(e instanceof DemoOnlyError) && (e.status === 0 || e.status === 401 || e.status >= 501 || /unavailable$/.test(e.code));
  return e instanceof TypeError && /fetch|network|load failed/i.test(e.message);
}

/** `from`: the api method (or link) that needs a real account; the card turns it into one line of words. */
export function announce(from: string) {
  try { window.dispatchEvent(new CustomEvent(DEMO_ONLY_EVENT, { detail: { from } })); } catch { /* no window */ }
}
export function raiseDemoOnly(from: string): DemoOnlyError { announce(from); return new DemoOnlyError(from); }

/** The same api, with every call's "needs a real account" failure turned into the DemoOnlyError and the card. Only the sample uses it. */
export function withDemoOnly(api: Api): Api {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(api) as [string, unknown][]) {
    out[k] = typeof v !== "function" ? v : (...a: unknown[]) => {
      let r: unknown;
      try { r = (v as (...x: unknown[]) => unknown).apply(api, a); } catch (e) { if (isDemoOnlyFailure(e)) throw raiseDemoOnly(k); throw e; }
      return r && typeof (r as Promise<unknown>).then === "function"
        ? (r as Promise<unknown>).catch((e: unknown) => { if (isDemoOnlyFailure(e)) throw raiseDemoOnly(k); throw e; })
        : r;
    };
  }
  return out as unknown as Api;
}

